import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  ImageCroppedEvent,
  ImageCropperComponent,
  OutputFormat,
} from 'ngx-image-cropper';
import { NGXLogger } from 'ngx-logger';
import { DialogComponent } from '../dialog/dialog.component';

const IMAGE_FILENAME = 'cropped-image.jpg';
const IMAGE_MIME_TYPE = 'image/jpeg';

@Component({
  selector: 'core-image-cropping',
  templateUrl: './image-cropping.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ImageCropperComponent, TranslocoDirective, DialogComponent],
})
export class ImageCroppingComponent implements OnDestroy, AfterViewInit {
  private logger = inject(NGXLogger);
  private elementRef = inject(ElementRef);

  @ViewChild(DialogComponent) dialogComponent!: DialogComponent;
  @Input() isOpen: boolean = false;
  @Output() modalClosed = new EventEmitter<void>();
  @Output() imageProcessed = new EventEmitter<File>();
  private onClose = new EventEmitter<boolean>();

  imageChangedEvent: Event | null = null;
  croppedImage: string | null = null;
  imageFormat: OutputFormat = 'jpeg';
  dialogTitle = 'image-cropping-title';
  confirmButtonText = 'core.buttons.save';
  closeButtonText = 'core.buttons.cancel';

  ngAfterViewInit() {
    if (this.isOpen) {
      this.dialogComponent.onOpen();
    }
  }

  ngOnDestroy() {
    this.elementRef.nativeElement.remove();
  }

  openModal(event: Event) {
    this.imageChangedEvent = event;
    this.isOpen = true;

    setTimeout(() => {
      if (this.dialogComponent) {
        this.logger.debug('Calling dialogComponent.onOpen()');
        this.dialogComponent.onOpen();
      } else {
        this.logger.error(
          'dialogComponent is undefined in openModal setTimeout',
        );
      }
    }, 0);
  }

  closeModal(success: boolean) {
    this.onClose.emit(success);
    this.isOpen = false;
    this.imageChangedEvent = null;
    this.croppedImage = null;
    this.modalClosed.emit();
  }

  onImageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.objectUrl || null;
    this.logger.debug('Image cropped:', event);
  }

  onSave() {
    this.applyCrop();
  }

  onDialogClose() {
    this.closeModal(false);
  }

  applyCrop() {
    if (this.croppedImage) {
      fetch(this.croppedImage)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to process the image');
          }
          return response.blob();
        })
        .then((blob) => {
          const file = new File([blob], IMAGE_FILENAME, {
            type: IMAGE_MIME_TYPE,
          });
          this.imageProcessed.emit(file);
          this.closeModal(true);
        })
        .catch((error) => {
          this.logger.error('Error converting cropped image to file:', error);
          this.closeModal(false);
        });
    }
  }
}
